import { CircularProgress, Collapse } from '@mui/material';
import { NoDataComponent } from 'apps/wrapper/src/components/shared/grid/no-data';
import { AppointmentRequestSummary } from 'apps/wrapper/src/services/appointment/types/appointment-request-summary';
import { FC, useEffect, useRef } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { TransitionGroup } from 'react-transition-group';
import { useApptRequestsWidget } from '../../../hooks/use-appt-requests-widget';
import { ApptRequestsWidgetRoutePath } from '../../../navigation/route-config';
import { ApptRequestListItem } from './appt-request-list-item/appt-request-list-item';
import { ApptRequestListWrapper } from './appt-request-list.styles';
import { EndOfList } from './end-of-list/end-of-list';

export const ApptRequestList: FC = () => {
  const {
    goTo,
    appointmentRequests,
    isLoadingMoreApptRequests,
    allApptRequestsLoaded,
    loadMoreApptRequests,
    listScrollTop,
    setListScrollTop
  } = useApptRequestsWidget();

  const scrollRef = useRef<Scrollbars>(null);

  const handleScroll = async (values: { top: number; scrollTop?: number }) => {
    if (isLoadingMoreApptRequests || allApptRequestsLoaded) return;

    if (values.top >= 0.9) await loadMoreApptRequests();
    // Save the pixel scrollTop if available
    if (typeof values.scrollTop === 'number' && values.scrollTop !== listScrollTop) setListScrollTop(values.scrollTop);
  };

  // Restore scroll position when the list mounts or when the data changes
  useEffect(() => {
    if (!scrollRef.current) return;
    const view = scrollRef.current.getScrollTop();
    if (listScrollTop && view !== listScrollTop) {
      scrollRef.current.scrollTop(listScrollTop);
    }
    // If no saved top, ensure we start at the top when list is first shown
    if (!listScrollTop) {
      scrollRef.current.scrollTop(0);
    }
  }, [scrollRef, appointmentRequests.length]);

  // On unmount, persist the current scroll position so it can be restored when returning
  useEffect(() => {
    return () => {
      const currentTop = scrollRef.current?.getScrollTop?.();
      if (typeof currentTop === 'number' && currentTop !== listScrollTop) setListScrollTop(currentTop);
    };
  }, [listScrollTop, setListScrollTop]);

  const handleApptRequestClick = (request: AppointmentRequestSummary) => {
    // Persist current scroll position before navigating away
    const currentTop = scrollRef.current?.getScrollTop?.();
    if (typeof currentTop === 'number' && currentTop !== listScrollTop) setListScrollTop(currentTop);

    let path = ApptRequestsWidgetRoutePath.REQUEST_DETAILS;

    if (request.appointmentType.toLowerCase() === 'reschedule') path = ApptRequestsWidgetRoutePath.RESCHEDULE_REQUEST;

    if (request.appointmentType.toLowerCase() === 'cancel') path = ApptRequestsWidgetRoutePath.CANCELLATION_REQUEST;

    goTo({ path, props: { apptRequestId: request.id } });
  };

  return (
    <ApptRequestListWrapper>
      <Scrollbars autoHide onScrollFrame={handleScroll} ref={scrollRef}>
        <TransitionGroup>
          {appointmentRequests.length > 0 &&
            appointmentRequests.map((request) => (
              <Collapse key={request.id} in>
                <ApptRequestListItem appointmentRequest={request} onClick={handleApptRequestClick} />
              </Collapse>
            ))}
        </TransitionGroup>
        {appointmentRequests.length === 0 && <NoDataComponent />}
        {isLoadingMoreApptRequests && (
          <CircularProgress sx={{ display: 'flex', margin: '20px auto', width: '32px', height: '32px' }} />
        )}
        <EndOfList />
      </Scrollbars>
    </ApptRequestListWrapper>
  );
};
